/* eslint-disable no-restricted-globals */
// isNaN global is different from Number.isNaN
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/isNaN

import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import { localeMapping } from '../../lib/localizedCountries';
import { getDateTime } from '../../lib/dates';

const deepClone = (object) => JSON.parse(JSON.stringify(object));

export const getUrlDetails = () => {
  const http = window.location.protocol;
  const slashes = http.concat('//');
  const hostURL = slashes.concat(window.location.hostname);

  return {
    http,
    slashes,
    hostURL,
  };
};

export const createLabelItems = ({
  labels,
  retailerName,
  allItems,
  totalReturned = { total_items: 0 },
}) => {
  let itemCount = 0;
  const { hostURL } = getUrlDetails();

  labels.forEach((returnLabel) => {
    const labelItems = returnLabel.returned_items;
    const newItems = [];

    returnLabel.url = `${hostURL}/returns/${retailerName}/viewlabel?return_id=${returnLabel.encrypted_return_id}`;

    returnLabel.return_items = {};

    labelItems.forEach((labelItem) => {
      const fullItem = {
        ...allItems.find((item) => item.item_id === labelItem.item_id),
      };

      if (fullItem) {
        fullItem.qty = labelItem.qty;
        itemCount += labelItem.qty;
        newItems.push(fullItem);
      }
    });

    returnLabel.return_items = newItems;
  });

  totalReturned.total_items = itemCount;

  return {
    labels,
    totalReturned,
  };
};

export const getEligibleItems = (items = [], retailerEligibilities = {}, arrayMethod) =>
  items.filter((item) =>
    Object.keys(retailerEligibilities)[arrayMethod]((type) => {
      if (!isEmpty(item.item_eligibilities)) {
        return item.item_eligibilities[type]?.is_eligible === retailerEligibilities[type];
      }

      return item[`${type}_eligibility`] === retailerEligibilities[type];
    }),
  );

export const getIneligibleItems = (items = [], retailerEligibilities = {}) =>
  items.filter((item) =>
    Object.keys(retailerEligibilities).every((type) => {
      if (!isEmpty(item.item_eligibilities)) {
        return item.item_eligibilities[type]?.is_eligible !== retailerEligibilities[type];
      }

      return item[`${type}_eligibility`] !== retailerEligibilities[type];
    }),
  );

export const parseDate = (input) => {
  try {
    if (input === null || typeof input === 'undefined') {
      throw new Error('Input cannot be null or undefined');
    }

    if (input instanceof Date) {
      if (isNaN(input.valueOf())) {
        throw new Error('Input is an invalid Date object');
      }
      return input;
    }

    if (typeof input === 'number' || !isNaN(input)) {
      const timestamp = Number.parseInt(input, 10);
      if (!isNaN(timestamp)) {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
          throw new Error('Input number is an invalid timestamp');
        }
        return date;
      }
    }

    if (typeof input === 'string') {
      const date = new Date(input);
      if (isNaN(date.getTime())) {
        throw new Error('Input string is an invalid date string');
      }
      return date;
    }

    throw new Error('Input type is not supported for date parsing');
  } catch (e) {
    console.error(e.message);
    return undefined;
  }
};

export const getByTimestamp = ({ tempItemsByReturnDate, locale }) => {
  const itemLocale = localeMapping({ locale, format: true });
  return Object.keys(tempItemsByReturnDate)
    .map((date) => ({
      date,
      formattedDate: getDateTime({ date, locale: itemLocale }),
      items: { ...tempItemsByReturnDate[date] },
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};

export const getEligibleItemsByReturnDate = (
  orderItems = [],
  shipments = [],
  retailerEligibilities,
  language,
  locale,
  exchangeEnabled,
) => {
  if (!orderItems || !orderItems.length || !shipments || !shipments.length) {
    return [];
  }
  const runningItemTotal = {};
  const totalItemQty = shipments.reduce(
    (acc, { shipment_items: itemsInfo, ship_by_date: shipByDate }) => {
      itemsInfo.forEach(({ product_sku: productSku, quantity }) => {
        acc.dateQty[shipByDate] = acc.dateQty[shipByDate] || {};
        if (typeof acc.skuQty[productSku] === 'undefined') {
          const orderItem = orderItems.find(({ product_sku }) => product_sku === productSku);
          // orderItems is just the eligible ones, !exist === already ineligible
          if (!orderItem) return;
          acc.skuQty[productSku] = {
            totalQty: orderItem.item_qty,
            shippedQty: quantity,
            remainingQty: orderItem.remaining_qty,
          };
        } else {
          acc.skuQty[productSku].shippedQty += quantity;
        }

        if (typeof acc.dateQty[shipByDate][productSku] !== 'undefined') {
          acc.dateQty[shipByDate][productSku] += quantity;
        } else {
          acc.dateQty[shipByDate][productSku] = quantity;
        }
      });
      return acc;
    },
    {
      dateQty: {},
      skuQty: {},
    },
  );
  const tempItemsByReturnDate = shipments.reduce((acc, current) => {
    const { shipment_items: itemsInfo, ship_id: shipId, ship_by_date: returnByDate } = current;
    if (itemsInfo.length) {
      itemsInfo.forEach(({ product_sku: productSku }) => {
        acc[returnByDate] = acc[returnByDate] || {};
        const newItem = orderItems.find(({ product_sku }) => product_sku === productSku);
        // Fully returned items no longer appear in orderItems
        if (!newItem) return;
        runningItemTotal[productSku] =
          typeof runningItemTotal[productSku] === 'undefined'
            ? totalItemQty.skuQty[productSku].shippedQty -
              totalItemQty.skuQty[productSku].remainingQty
            : runningItemTotal[productSku];
        acc[returnByDate][productSku] = acc[returnByDate][productSku] || {
          ...newItem,
          ship_by_date: returnByDate,
          remaining_qty: totalItemQty.dateQty[returnByDate][productSku],
          return_qty: 0,
          ship_id: shipId,
        };
        if (runningItemTotal[productSku] <= 0 || acc[returnByDate][productSku].remaining_qty <= 0) {
          return;
        }
        // deduct shipped <--> remaining difference from display
        if (runningItemTotal[productSku] > acc[returnByDate][productSku].remaining_qty) {
          runningItemTotal[productSku] -= acc[returnByDate][productSku].remaining_qty;
          acc[returnByDate][productSku].remaining_qty = 0;
        } else {
          acc[returnByDate][productSku].remaining_qty -= runningItemTotal[productSku];
          runningItemTotal[productSku] = 0;
        }
        acc[returnByDate][productSku].is_shipment_item = true;
      });
    }
    return acc;
  }, {});

  // remove products w/ 0 qty && dates w/ 0 products && items that are ineligible
  Object.keys(tempItemsByReturnDate).forEach((date) => {
    Object.keys(tempItemsByReturnDate[date]).forEach((sku) => {
      const currentItem = tempItemsByReturnDate[date][sku];
      const itemIsEligible =
        getEligibleItems([currentItem], retailerEligibilities, exchangeEnabled ? 'some' : 'every')
          .length > 0;

      if (!currentItem.remaining_qty || !itemIsEligible) {
        delete tempItemsByReturnDate[date][sku];
      }
    });
    if (Object.keys(tempItemsByReturnDate[date]).length === 0) {
      delete tempItemsByReturnDate[date];
    }
  });

  // format object into array (Earliest --> latest ship_by_date)
  const byTimestamp = getByTimestamp({ tempItemsByReturnDate, locale });

  // calculate ineligible items from (total item_qty - shipped item_qty)
  const ineligibleByDay = Object.keys(totalItemQty.skuQty).reduce((acc, sku) => {
    const { totalQty, shippedQty } = totalItemQty.skuQty[sku];
    const ineligibleRemainder = totalQty - shippedQty;
    if (ineligibleRemainder <= 0) return acc;
    const existingItem = deepClone(orderItems.find(({ product_sku }) => product_sku === sku));
    Object.keys(retailerEligibilities).forEach((type) => {
      if (!isEmpty(existingItem.item_eligibilities)) {
        existingItem.item_eligibilities[type].is_eligible = false;
        existingItem.return_eligibility_message = language['returns.step_1.ineligible.unshipped'];
      } else {
        existingItem[`${type}_eligibility`] = false;
        existingItem[`${type}_eligibility_message`] =
          language['returns.step_1.ineligible.unshipped'];
      }
    });
    acc.push({
      ...existingItem,
      item_qty: ineligibleRemainder,
      remaining_qty: ineligibleRemainder,
      return_qty: 0,
    });
    return acc;
  }, []);

  const byDay = byTimestamp.reduce((acc, current) => {
    const existing = acc.find(({ formattedDate }) => formattedDate === current.formattedDate);
    if (existing) {
      Object.keys(current.items).forEach((key) => {
        if (existing.items[key]) {
          existing.items[key].remaining_qty += current.items[key].remaining_qty;
          existing.items[key].return_qty += current.items[key].return_qty;
        } else {
          existing.items[key] = current.items[key];
        }
      });
    } else {
      acc.push(deepClone(current));
    }
    return acc;
  }, []);
  return {
    byDay,
    byTimestamp,
    ineligibleByDay,
  };
};

export const getFinalReduce = ({ iterable, itemsById }) =>
  iterable.reduce((acc, current) => {
    const { date, items: itemsForDate } = current;
    acc[date] = acc[date] || {};
    forEach(
      itemsForDate,
      (
        { remaining_qty: dateQty, ship_id: shipId, item_id: itemId, ship_by_date: shipByDate },
        productSku,
      ) => {
        if (!itemsById[productSku]) return;
        const {
          return_qty: toReturn = 0,
          ineligibilityInfo = [],
          return_desc: returnDesc,
          itemStatus,
        } = itemsById[productSku];
        acc[date][productSku] = acc[date][productSku] || {
          item_id: itemId,
          return_qty: 0,
          remaining_qty: dateQty,
          ship_id: shipId,
          product_sku: productSku,
          ship_by_date: shipByDate,
          ineligibilityInfo,
        };
        if (toReturn <= 0) return;
        if (toReturn >= dateQty) {
          itemsById[productSku].return_qty -= dateQty;
          acc[date][productSku].return_qty += dateQty;
          acc[date][productSku].remaining_qty = 0;
          acc[date][productSku].selected = true;
        } else {
          acc[date][productSku].remaining_qty -= toReturn;
          acc[date][productSku].return_qty += toReturn;
          itemsById[productSku].return_qty = 0;
          acc[date][productSku].selected = true;
        }

        acc[date][productSku].return_desc = returnDesc;
        acc[date][productSku].itemStatus = itemStatus;
      },
    );
    return acc;
  }, {});

export const appendParamsToUrl = (url, params) => {
  const urlObj = new URL(url);
  const searchParams = new URLSearchParams(urlObj.search);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null) {
      searchParams.set(key, value);
    }
  });

  return `${urlObj.origin}${urlObj.pathname}?${searchParams}`;
};
